import { useState, useEffect } from 'react';
import { Copy, Download, Settings2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SlidePanel } from '@/shared/components';
import { useRocks, useOwnerOptions } from '../hooks/useRocks';
import { useTeams } from '../hooks/useTeams';
import {
  useRockListTable,
  copyRocksToClipboard,
  exportRocksCSV,
} from '../hooks/useRockListTable';
import { RockListView } from '../components/RockListView';
import { RockPlanningBoard } from '../components/RockPlanningBoard';
import { RockDetailPanel } from '../components/RockDetailPanel';
import { StagesTable } from '@/features/os-stages/components/StagesTable';
import type { Rock } from '../types';

type ActiveTab = 'list' | 'board' | 'archive' | 'stages' | 'guide';

const TABS: { value: ActiveTab; label: string }[] = [
  { value: 'list', label: 'Rocks' },
  { value: 'board', label: 'Kanban' },
  { value: 'archive', label: 'Archive' },
  { value: 'stages', label: 'Stages Reference' },
  { value: 'guide', label: 'Guide' },
];

const PANEL_STORAGE_KEY = 'atek_rocks_panel_width';
const ACTIVE_TAB_STORAGE_KEY = 'atek_rocks_active_tab';

const VALID_TABS: ActiveTab[] = ['list', 'board', 'archive', 'stages', 'guide'];

export function RocksPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>(() => {
    const saved = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY) as ActiveTab | null;
    return saved && VALID_TABS.includes(saved) ? saved : 'list';
  });
  const [selectedRockId, setSelectedRockId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<'view' | 'create'>('view');
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  const archived = activeTab === 'archive' ? 'true' : 'false';
  const { data: rocks = [], isLoading } = useRocks({ archived });
  const { data: owners = [] } = useOwnerOptions();
  const { data: teamsData = [] } = useTeams();

  const table = useRockListTable(rocks, owners, teamsData);

  const openRock = (rock: Rock) => {
    setSelectedRockId(rock.id);
    setPanelMode('view');
    setPanelOpen(true);
  };

  const openCreate = () => {
    setSelectedRockId(null);
    setPanelMode('create');
    setPanelOpen(true);
  };

  const handlePanelClose = (open: boolean) => {
    setPanelOpen(open);
    if (!open) {
      setSelectedRockId(null);
      setPanelMode('view');
    }
  };

  const handleCreated = (id: string) => {
    setSelectedRockId(id);
    setPanelMode('view');
  };

  const showListToolbar = activeTab === 'list' || activeTab === 'archive';

  const handleCopy = () => {
    copyRocksToClipboard(table.sortedRocks, table.columns, table.ctx);
    toast.success('Copied to clipboard');
  };

  const handleExportCsv = () => {
    exportRocksCSV(table.sortedRocks, table.columns, table.ctx);
    toast.success(`Exported ${table.sortedRocks.length} rows`);
  };

  return (
    <div className="space-y-4">
      {/* Tab bar + toolbar (compact single row, matching KPI page) */}
      <div className="flex items-center justify-between gap-2 border-b border-border flex-wrap">
        <div className="flex items-center gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                activeTab === tab.value
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {showListToolbar && (
          <div className="flex items-center gap-2 flex-wrap pb-1.5">
            <Button variant="outline" size="sm" onClick={handleCopy}>
              <Copy className="h-4 w-4 mr-1" />
              Copy
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCsv}>
              <Download className="h-4 w-4 mr-1" />
              CSV
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="h-4 w-4 mr-1" />
                  Columns
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table.columns.filter((c) => !c.noFilter).map((col) => (
                  <DropdownMenuCheckboxItem
                    key={col.key}
                    checked={table.isVisible(col.key)}
                    onCheckedChange={() => table.toggleColumn(col.key)}
                  >
                    {col.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" />
              New Rock
            </Button>
          </div>
        )}
      </div>

      {/* Main content area with optional side panel */}
      <div className="flex min-h-[calc(100vh-240px)]">
        {/* Left: tab content */}
        <div className="flex-1 min-w-0 overflow-auto">
          {activeTab === 'list' && (
            <RockListView
              sortedRocks={table.sortedRocks}
              visibleColumns={table.visibleColumns}
              columnFilters={table.columnFilters}
              ctx={table.ctx}
              sortKey={table.sortKey}
              sortDirection={table.sortDirection}
              isLoading={isLoading}
              onSort={table.handleSort}
              onSetColumnFilter={table.setColumnFilter}
              onEditRock={openRock}
              onSelectRock={openRock}
              onCreateRock={openCreate}
            />
          )}

          {activeTab === 'board' && (
            <div>
              {isLoading ? (
                <div className="flex gap-3 overflow-x-auto pb-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="min-w-[260px] w-[260px] h-80 rounded-lg bg-muted animate-pulse" />
                  ))}
                </div>
              ) : (
                <RockPlanningBoard rocks={rocks} onEditRock={openRock} />
              )}
            </div>
          )}

          {activeTab === 'archive' && (
            <RockListView
              sortedRocks={table.sortedRocks}
              visibleColumns={table.visibleColumns}
              columnFilters={table.columnFilters}
              ctx={table.ctx}
              sortKey={table.sortKey}
              sortDirection={table.sortDirection}
              isLoading={isLoading}
              onSort={table.handleSort}
              onSetColumnFilter={table.setColumnFilter}
              onEditRock={openRock}
              onSelectRock={openRock}
              onCreateRock={openCreate}
            />
          )}

          {activeTab === 'stages' && <StagesTable />}

          {activeTab === 'guide' && <RocksGuide />}
        </div>

        <SlidePanel open={panelOpen} storageKey={PANEL_STORAGE_KEY}>
          <RockDetailPanel
            key={panelMode === 'create' ? 'create' : selectedRockId}
            rockId={selectedRockId}
            mode={panelMode}
            open={panelOpen}
            onOpenChange={handlePanelClose}
            onCreated={handleCreated}
          />
        </SlidePanel>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Guide tab content
// ─────────────────────────────────────────────────────────────────────────────

function RocksGuide() {
  return (
    <div className="rounded-lg border bg-card p-6 space-y-6 text-sm max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold">What is a Rock?</h2>
        <p className="text-muted-foreground mt-2">
          A Rock is a <strong>90-day priority</strong> — the most important thing
          you or your team must accomplish this quarter to move the company
          forward. The name comes from Stephen Covey's jar analogy: if you don't
          fit the big rocks in first, the sand and pebbles of daily work will
          crowd them out. In EOS, every quarter each person and each team picks
          a small set of Rocks and commits to finishing them.
        </p>
      </div>

      <div>
        <h3 className="font-semibold">Rules of a good Rock</h3>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
          <li><strong>SMART</strong> — Specific, Measurable, Attainable, Relevant, Time-bound.</li>
          <li><strong>One owner.</strong> Shared accountability means no accountability.</li>
          <li><strong>Done by quarter-end</strong> — 90 days, not rolling.</li>
          <li><strong>3–7 per person, per team</strong> — if everything is a priority, nothing is.</li>
          <li><strong>Binary outcome</strong> — at the end of the quarter the Rock is either <em>done</em> or <em>not done</em>. No partial credit.</li>
          <li><strong>Reviewed weekly</strong> in the Level 10 meeting — on track / off track / done.</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold">What is NOT a Rock</h3>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
          <li>
            <strong>Day-to-day work or BAU</strong> — your job description isn't
            a Rock. A Rock is a change, a push, something that wouldn't happen
            without deliberate focus.
          </li>
          <li>
            <strong>A To-Do</strong> — if it can be done in a week or less, it's
            a To-Do, not a Rock.
          </li>
          <li>
            <strong>An Issue</strong> — unresolved problems belong on the Issues
            list (IDS: Identify, Discuss, Solve), not on the Rock sheet.
          </li>
          <li>
            <strong>A KPI or ongoing metric</strong> — "keep uptime above 99.9%"
            is a scorecard number, not a Rock. A Rock is a finite project with a
            finish line.
          </li>
          <li>
            <strong>A vague aspiration</strong> — "improve customer success"
            isn't a Rock. "Ship NPS survey to top 50 accounts and review
            responses by June 30" is.
          </li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold">Weekly status in the L10</h3>
        <p className="text-muted-foreground mt-2">
          Every Rock is reviewed in the Level 10 meeting and called either:
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1 text-muted-foreground">
          <li><strong>On track</strong> — the owner is confident it will be done by quarter-end.</li>
          <li><strong>Off track</strong> — the owner is not confident; it drops onto the Issues list for IDS.</li>
          <li><strong>Done</strong> — finished, celebrated, closed.</li>
        </ul>
      </div>

      <div>
        <h3 className="font-semibold">At quarter end</h3>
        <p className="text-muted-foreground mt-2">
          Score the quarter — how many Rocks were actually completed? Healthy
          EOS teams aim for <strong>80%+ Rock completion</strong>. Anything
          lower means Rocks were either too ambitious, under-prioritized, or
          not truly owned. Adjust next quarter accordingly.
        </p>
      </div>
    </div>
  );
}
